<script setup lang="ts">
// NPM
import { computed, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Types
import type { PropType } from 'vue';
import type { SlideOverStatusHelperRole } from './slide-over/types';

// UI Components
import SlideOverShell from './slide-over/SlideOverShell.vue';
import SlideOverHero from './slide-over/SlideOverHero.vue';
import SlideOverDeactivateForm from './slide-over/SlideOverDeactivateForm.vue';
import SlideOverReactivateForm from './slide-over/SlideOverReactivateForm.vue';
import SlideOverDefaultView from './slide-over/SlideOverDefaultView.vue';

// Composables
import { useSlideOverCampaignCriteria } from './slide-over/composables/useSlideOverCampaignCriteria';
import { useSlideOverDeactivation } from './slide-over/composables/useSlideOverDeactivation';
import { useSlideOverReactivation } from './slide-over/composables/useSlideOverReactivation';

// Stores
import { useCommonStore } from '@/stores/common';
import { useUserStore } from '@/stores/user';
const commonStore = useCommonStore();
const userStore = useUserStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  campaignId: {
    type: Number,
    default: null,
  },
  open: {
    type: Boolean,
    default: false,
  },
  statusHelperRole: {
    type: String as PropType<SlideOverStatusHelperRole>,
    default: 'join-campaign',
  },
  deactivateCampaignUnstartedRelations: {
    type: Number,
    default: 0,
  },
});

// Reactive variables
const displayDeactivationForm = ref(false);
const displayReactivationForm = ref(false);
const displaySuccessView = ref(false);

// Composable wiring
const campaignIdRef = toRef(props, 'campaignId');
const deactivateCampaignUnstartedRelationsRef = toRef(
  props,
  'deactivateCampaignUnstartedRelations'
);

const {campaignCriteria,
  getCriteriaOptions,
  buildInitialReactivationCriteria} = useSlideOverCampaignCriteria({
  campaignId: campaignIdRef,
  userStore,
});

const onFlowSuccess = () => {
  displayDeactivationForm.value = false;
  displayReactivationForm.value = false;
  displaySuccessView.value = true;
};

const {
  deactivationForm,
  deactivationV$,
  deactivationOptions,
  isDeactivationSubmitting,
  submitDeactivation,
  resetDeactivation,
} = useSlideOverDeactivation({
  campaignId: campaignIdRef,
  deactivateCampaignUnstartedRelations: deactivateCampaignUnstartedRelationsRef,
  userStore,
  t,
  onSuccess: onFlowSuccess,
});

const {
  reactivationForm,
  hasAllCriteriaSelected,
  isReactivationSubmitting,
  initializeReactivationCriteria,
  submitReactivation,
  resetReactivation,
} = useSlideOverReactivation({
  campaignId: campaignIdRef,
  campaignCriteria,
  buildInitialReactivationCriteria,
  userStore,
  onSuccess: onFlowSuccess,
});

const isLoading = computed(
  () => isDeactivationSubmitting.value || isReactivationSubmitting.value
);

const modalText = computed(() => {
  if (
    props.statusHelperRole === 'deactivate-campaign' &&
    !displaySuccessView.value
  ) {
    return {
      title: t('slideOverModal.deactivate.stop'),
      description: t('slideOverModal.deactivate.result'),
    };
  } else if (
    props.statusHelperRole === 'deactivate-campaign' &&
    displaySuccessView.value
  ) {
    return {
      title: t('slideOverModal.deactivate.anymore'),
      description: t('slideOverModal.deactivate.thanks'),
    };
  } else if (props.statusHelperRole === 'join-campaign') {
    return {
      title: t('slideOverModal.join.involved'),
      description: t('slideOverModal.join.thank'),
    };
  } else if (
    props.statusHelperRole === 'reactivate-campaign' &&
    !displaySuccessView.value
  ) {
    return {
      title: t('slideOverModal.reactivate.again'),
      description: t('slideOverModal.reactivate.support'),
    };
  } else if (
    props.statusHelperRole === 'reactivate-campaign' &&
    displaySuccessView.value
  ) {
    return {
      title: t('slideOverModal.reactivate.back'),
      description: t('slideOverModal.join.thank'),
    };
  } else {
    return {
      title: '',
      description: '',
    };
  }
});

// Watchers
// Watch for campaign changes to reinitialize criteria
watch(
  () => props.campaignId,
  () => {
    if (displayReactivationForm.value) {
      initializeReactivationCriteria();
    }
  }
);

// Event listeners
const onCloseSlideOverModal = async (closeType?: string) => {
  if (displaySuccessView.value) {
    closeType = '';
  }

  commonStore.closeSlideOverModal(closeType);

  // setTimeout helps to avoid a glitch where the slide over modal is closed before the animation is finished
  setTimeout(() => {
    displayDeactivationForm.value = false;
    displayReactivationForm.value = false;
    displaySuccessView.value = false;
    resetDeactivation();
    resetReactivation();
  }, 1000);
};

const onDeactivateCampaign = async () => {
  await submitDeactivation();
};

const onReactivateCampaign = async () => {
  await submitReactivation();
};

const onDisplayDeactivationForm = () => {
  displayDeactivationForm.value = true;
};

const onDisplayReactivationForm = () => {
  displayReactivationForm.value = true;
  initializeReactivationCriteria();
};
</script>

<template>
  <SlideOverShell :open="open" @close="onCloseSlideOverModal('cancel')">
    <div class="rounded-t-2xl bg-background-default shadow-xl lg:rounded-2xl">
      <SlideOverHero
        v-if="!displayReactivationForm"
        :status-helper-role="statusHelperRole"
      />

      <div class="mx-auto max-w-md px-4 pb-14 lg:p-6">
        <SlideOverDeactivateForm
          v-if="displayDeactivationForm"
          :deactivate-campaign-unstarted-relations="
            deactivateCampaignUnstartedRelations
          "
          :form="deactivationForm"
          :options="deactivationOptions"
          :validation="deactivationV$"
          :is-loading="isLoading"
          @confirm="onDeactivateCampaign"
          @cancel="onCloseSlideOverModal('cancel')"
        />

        <SlideOverReactivateForm
          v-else-if="displayReactivationForm"
          :campaign-criteria="campaignCriteria"
          :form="reactivationForm"
          :get-criteria-options="getCriteriaOptions"
          :has-all-criteria-selected="hasAllCriteriaSelected"
          :is-loading="isLoading"
          @confirm="onReactivateCampaign"
          @cancel="onCloseSlideOverModal('cancel')"
        />

        <SlideOverDefaultView
          v-else
          :status-helper-role="statusHelperRole"
          :modal-text="modalText"
          :display-success-view="displaySuccessView"
          @show-deactivate-form="onDisplayDeactivationForm"
          @show-reactivate-form="onDisplayReactivationForm"
          @cancel="onCloseSlideOverModal('cancel')"
          @close="onCloseSlideOverModal()"
        />
      </div>
    </div>
  </SlideOverShell>
</template>
